import React from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer, LabelList,
} from "recharts";

const OUTCOME_COLORS = { positive: "#059669", negative: "#e11d48", mixed: "#d97706", unclear: "#94a3b8" };

function shortName(s, n = 18) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

export function CaseSimilarityChart({ cases }) {
  const data = (cases || []).map((c) => ({
    name: shortName(c.company),
    full: c.company,
    similarity: c.similarity?.overall || 0,
    outcome: c.outcome_category || "unclear",
  }));
  if (!data.length) return null;
  const h = Math.max(160, data.length * 46);
  return (
    <div style={{ width: "100%", height: h }} data-testid="case-similarity-chart">
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" margin={{ top: 6, right: 40, bottom: 6, left: 8 }}>
          <CartesianGrid horizontal={false} stroke="#f1f5f9" strokeDasharray="3 3" />
          <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11, fill: "#94a3b8", fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: "#334155" }} axisLine={false} tickLine={false} />
          <Tooltip cursor={{ fill: "#f8fafc" }}
            contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }}
            formatter={(v, _n, p) => [`${v}% match · ${p.payload.outcome}`, p.payload.full]} />
          <Bar dataKey="similarity" radius={[0, 4, 4, 0]} barSize={18}>
            {data.map((d, i) => <Cell key={i} fill={OUTCOME_COLORS[d.outcome]} />)}
            <LabelList dataKey="similarity" position="right" style={{ fontSize: 11, fill: "#64748b", fontFamily: "JetBrains Mono" }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function parsePct(v) {
  if (v == null) return null;
  const n = parseFloat(String(v).replace("%", "").replace(/[^0-9.\-]/g, ""));
  return Number.isNaN(n) ? null : n;
}

export function ActualResultsChart({ actual }) {
  const fields = [
    ["revenue_change", "Revenue"], ["sales_change", "Sales"], ["conversion_change", "Conversion"],
    ["cost_change", "Cost"], ["profit_change", "Profit"],
  ];
  const data = fields
    .map(([k, label]) => ({ label, value: parsePct(actual?.[k]), raw: actual?.[k] }))
    .filter((d) => d.value !== null && !Number.isNaN(d.value));
  if (!data.length) return null;
  return (
    <div style={{ width: "100%", height: 220 }} data-testid="actual-results-chart">
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 16, right: 12, bottom: 6, left: 0 }}>
          <CartesianGrid vertical={false} stroke="#f1f5f9" strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#334155" }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 11, fill: "#94a3b8", fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} unit="%" />
          <Tooltip cursor={{ fill: "#f8fafc" }} contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 }}
            formatter={(v, _n, p) => [p.payload.raw, "Your change"]} />
          <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={40}>
            {data.map((d, i) => <Cell key={i} fill={d.value >= 0 ? "#059669" : "#e11d48"} />)}
            <LabelList dataKey="raw" position="top" style={{ fontSize: 11, fill: "#64748b" }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
